import React, { useEffect, useState } from "react";
import axios from "axios";
import "../css/PMCArticleComponent.css";
import RightSidebar from "./RightSidebar";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";

const PAGE_SIZE = 20;
const DEBOUNCE_DELAY = 500;

const yearIntervals = [
  { label: "1970–1980", start: 1970, end: 1980 },
  { label: "1981–1990", start: 1981, end: 1990 },
  { label: "1991–2000", start: 1991, end: 2000 },
  { label: "2001–2010", start: 2001, end: 2010 },
  { label: "2011–2020", start: 2011, end: 2020 },
  { label: "2021–2025", start: 2021, end: 2025 },
];

const PMCArticleComponent = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [selectedInterval, setSelectedInterval] = useState(null);
  const [expandedAbstracts, setExpandedAbstracts] = useState({});
  const navigate = useNavigate();

  // debounce search
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedQuery(searchQuery), DEBOUNCE_DELAY);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  // fetch articles
  useEffect(() => {
    const fetchArticles = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = { page, page_size: PAGE_SIZE };

        if (debouncedQuery) params.q = debouncedQuery;
        if (selectedInterval) {
          params.year_start = selectedInterval.start;
          params.year_end = selectedInterval.end;
        }

        const res = await axios.get("http://197.156.76.122/api/pmc-articles/", {
          params,
        });

        setArticles(res.data.results || []);
        setTotalCount(res.data.count || 0);
        setTotalPages(Math.ceil((res.data.count || 0) / PAGE_SIZE));
      } catch (err) {
        console.error(err);
        setError("Failed to fetch PMC articles.");
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, [page, debouncedQuery, selectedInterval]);

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) setPage(newPage);
  };

  const toggleAbstract = (id) => {
    setExpandedAbstracts((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const formatCitation = (article) => {
    const vol = article.volume || "";
    const issue = article.issue ? `(${article.issue})` : "";
    const pages = article.pages ? `: ${article.pages}` : "";
    const date = article.date_of_publication || "";
    const journal =
      article.journal_abbr || article.journal_title || "Unknown Journal";

    return `${journal}. ${date}${vol || issue || pages ? "; " : ""}${vol}${issue}${pages}.`;
  };

  // ✅ NEW SAFE DOWNLOAD FUNCTION (HTTP + HTTPS OK)
  const handleDownload = async (url, filename = "pmc_articles.zip") => {
    try {
      const res = await axios.get(url, {
        responseType: "blob",
      });

      const blob = new Blob([res.data]);
      const downloadUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", filename);

      document.body.appendChild(link);
      link.click();
      link.remove();

      setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 1000);
    } catch (err) {
      console.error("Download failed:", err);
      alert("Download failed");
    }
  };

  const startItem = totalCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endItem = Math.min(page * PAGE_SIZE, totalCount);

  const renderPagination = () => (
    <div className="pmc-pagination">
      <button className="back-button" onClick={() => navigate("/")}>
        <FaArrowLeft /> Back to Home
      </button>

      <button onClick={() => handlePageChange(1)} disabled={page === 1}>
        ⏮ First
      </button>
      <button onClick={() => handlePageChange(page - 1)} disabled={page === 1}>
        ← Previous
      </button>

      <span>
        Page {page} of {totalPages}
      </span>

      <button onClick={() => handlePageChange(page + 1)} disabled={page === totalPages}>
        Next →
      </button>
      <button onClick={() => handlePageChange(totalPages)} disabled={page === totalPages}>
        Last ⏭
      </button>
    </div>
  );

  return (
    <div className="pmc-page-wrapper">
      <header className="pmc-top-header">
        <h1>PMC Articles Result</h1>
      </header>

      <div className="pmc-page">
        <aside className="pmc-filters-sidebar">
          <h3>Filters</h3>
          <h4>By Year Interval</h4>

          <label>
            <input
              type="radio"
              checked={!selectedInterval}
              onChange={() => {
                setSelectedInterval(null);
                setPage(1);
              }}
            />
            All
          </label>

          {yearIntervals.map((interval) => (
            <label key={interval.label}>
              <input
                type="radio"
                checked={selectedInterval?.label === interval.label}
                onChange={() => {
                  setSelectedInterval(interval);
                  setPage(1);
                }}
              />
              {interval.label}
            </label>
          ))}
        </aside>

        <main className="pmc-container">
          <div className="pmc-search-bar">
            <input
              type="text"
              placeholder="Search by title, PMID, or author..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
            />
          </div>

          {renderPagination()}

          <div className="pmc-summary">
            Items: {startItem} to {endItem} of {totalCount}
          </div>

          {loading ? (
            <p>Loading articles...</p>
          ) : error ? (
            <p>{error}</p>
          ) : articles.length > 0 ? (
            <>
              {articles.map((article, idx) => {
                const itemNumber = startItem + idx;
                const pmcid = article.pmcid || "";
                const pmid = article.pmid || "";
                const isExpanded =
                  expandedAbstracts[pmcid || pmid] || false;

                return (
                  <div className="pmc-article pmc-card" key={pmcid || idx}>
                    <div>
                      <strong>
                        {itemNumber}. {article.title}
                      </strong>
                    </div>

                    <p>{formatCitation(article)}</p>

                    <p>
                      <strong>PMCID:</strong> {pmcid || "N/A"}
                    </p>

                    {pmid && (
                      <p>
                        <strong>PMID:</strong> {pmid}
                      </p>
                    )}

                    {article.abstract && (
                      <p>
                        <strong>Abstract:</strong>{" "}
                        {isExpanded
                          ? article.abstract
                          : `${article.abstract.slice(0, 250)}...`}

                        {article.abstract.length > 250 && (
                          <button
                            onClick={() => toggleAbstract(pmcid || pmid)}
                          >
                            {isExpanded ? "Show Less" : "Show More"}
                          </button>
                        )}
                      </p>
                    )}

                    {article.pdf_url && (
                      <button
                        onClick={() =>
                          handleDownload(article.pdf_url, "pmc_article.pdf")
                        }
                      >
                        📥 Download PDF
                      </button>
                    )}
                  </div>
                );
              })}

              {renderPagination()}
            </>
          ) : (
            <p>No articles found.</p>
          )}
        </main>

        <aside className="pmc-sidebar">
          <RightSidebar />
        </aside>
      </div>
    </div>
  );
};

export default PMCArticleComponent;
